import { Children, createContext, isValidElement, useContext, type ReactNode } from 'react';

/** Dentro de uma FaixaIndicadores o indicador vira cartao; fora, celula da grade antiga. */
const EmFaixa = createContext(false);

type Variacao = { texto: string; direcao: 'sobe' | 'desce' | 'igual' } | null;

/**
 * Numero de destaque (stat tile). A variacao e opcional e sempre vem com seta
 * e sinal, nunca so com cor.
 *
 * Na `FaixaIndicadores` cada indicador e um cartao, e o primeiro pode ser o de
 * `destaque` (fundo Abismo): o numero que responde a pergunta da pagina. Fora
 * dela (Ficha, Comparar), e a celula com filete da grade de linhas finas.
 */
export function Indicador({
  rotulo,
  valor,
  complemento,
  variacao,
  destaque = false,
  icone,
}: {
  rotulo: string;
  valor: ReactNode;
  complemento?: ReactNode;
  variacao?: Variacao;
  /** So na FaixaIndicadores, e so no primeiro. */
  destaque?: boolean;
  /** Icone decorativo no canto (so na FaixaIndicadores). */
  icone?: ReactNode;
}) {
  const cartao = useContext(EmFaixa);
  const linhaDeApoio = (complemento || variacao) && (
    <p
      className={`numero text-xs leading-snug ${cartao ? 'mt-2' : 'mt-0.5'} ${destaque ? 'text-hero-apagado' : 'text-apagado'}`}
    >
      {variacao && (
        <span className={`mr-1.5 font-medium ${destaque ? 'text-sobre-hero' : 'text-texto'}`}>
          {variacao.direcao === 'sobe' ? '▲' : variacao.direcao === 'desce' ? '▼' : '='} {variacao.texto}
        </span>
      )}
      {complemento}
    </p>
  );

  if (!cartao) {
    return (
      <div className="min-w-0 border-l-2 border-destaque bg-superficie px-4 py-3 max-sm:odd:last:col-span-2">
        <p className="rotulo-secao leading-tight">{rotulo}</p>
        <p className="numero mt-1 text-2xl font-semibold tracking-tight text-marca">{valor}</p>
        {linhaDeApoio}
      </div>
    );
  }

  return (
    <div
      className={`min-w-0 rounded-(--radius-painel) border p-4 shadow-cartao ${
        destaque ? 'col-span-2 border-hero-borda bg-hero text-sobre-hero lg:col-span-1' : 'border-borda bg-superficie'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`text-[12.5px] leading-snug font-semibold ${destaque ? 'text-sobre-hero' : 'text-texto'}`}>
          {rotulo}
        </p>
        {icone && (
          <span
            aria-hidden
            className={`grid size-8 shrink-0 place-items-center rounded-[9px] ${
              destaque ? 'bg-nevoa text-abismo' : 'border border-borda bg-superficie-2 text-apagado'
            }`}
          >
            {icone}
          </span>
        )}
      </div>
      <p
        className={`numero mt-2.5 truncate font-bold tracking-tight ${
          destaque
            ? 'text-[clamp(24px,2.1vw,30px)] leading-tight'
            : 'text-[clamp(20px,1.7vw,25px)] leading-tight text-marca'
        }`}
      >
        {valor}
      </p>
      {linhaDeApoio}
    </div>
  );
}

// Classes literais: o Tailwind so gera o que aparece escrito no codigo.
const COM_DESTAQUE: Record<number, string> = {
  2: 'lg:grid-cols-[1.45fr_minmax(0,1fr)]',
  3: 'lg:grid-cols-[1.45fr_repeat(2,minmax(0,1fr))]',
  4: 'lg:grid-cols-[1.45fr_repeat(3,minmax(0,1fr))]',
  5: 'lg:grid-cols-[1.45fr_repeat(4,minmax(0,1fr))]',
};
const IGUAIS: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

/**
 * Faixa de 2 a 5 indicadores em cartoes. No desktop, uma linha (o de destaque
 * mais largo); abaixo de `lg`, 2 colunas, com o destaque ocupando a linha toda
 * e o ultimo, se sobrar sozinho, tambem.
 */
export function FaixaIndicadores({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  const itens = Children.toArray(children).filter(isValidElement);
  const comDestaque = !!(itens[0]?.props as { destaque?: boolean } | undefined)?.destaque;
  const n = Math.min(Math.max(itens.length, 2), 5);
  const resto = comDestaque ? itens.length - 1 : itens.length;
  return (
    <EmFaixa.Provider value>
      <section
        aria-label={rotulo}
        className={`grid grid-cols-2 gap-3 lg:gap-3.5 ${comDestaque ? COM_DESTAQUE[n] : IGUAIS[n]} ${
          resto % 2 === 1 ? 'max-lg:[&>*:last-child]:col-span-2' : ''
        }`}
      >
        {itens}
      </section>
    </EmFaixa.Provider>
  );
}
